import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const CertificationBadges = () => {
  const certifications = [
    {
      id: 1,
      name: "AWS Certified Solutions Architect",
      provider: "Amazon Web Services",
      icon: "Cloud",
      color: "from-orange-500 to-yellow-500",
      level: "Professional",
      validUntil: "2025-12-31",
      holders: ["Anbu Liyon"]
    },
    {
      id: 2,
      name: "MySQL Database",
      issuer: "Oracle",
      year: "2024",
      icon: "Database",
      color: "from-blue-500 to-cyan-500",
      level: "Professional",
      validUntil: "2025-08-15",
      holders: ["Giridharan"]
    },
    {
      id: 3,
      name: "React Developer Certification",
      provider: "Meta",
      icon: "Code2",
      color: "from-cyan-500 to-blue-500",
      level: "Advanced",
      validUntil: "2025-10-20",
      holders: ["Anbu Liyon", "Giridharan"]
    },
    {
      id: 4,
      name: "Node.js Application Developer",
      provider: "OpenJS Foundation",
      icon: "Server",
      color: "from-green-500 to-emerald-500",
      level: "Professional",
      validUntil: "2025-09-30",
      holders: ["Giridharan"]
    },
    {
      id: 5,
      name: "MongoDB Certified Developer",
      provider: "MongoDB Inc.",
      icon: "Database",
      color: "from-green-600 to-green-400",
      level: "Associate",
      validUntil: "2025-11-15",
      holders: ["Anbu Liyon"]
    },
    {
      id: 6,
      name: "AI Prompt Engineer",
      issuer: "OpenAI",
      year: "2024",
      icon: "Sparkles",
      color: "from-purple-500 to-pink-500",
      level: "Certified",
      validUntil: "2025-07-30",
      holders: ["Anbu Liyon", "Giridharan"]
    }
  ];

  const learningCommitments = [
    {
      title: "Weekly Tech Talks",
      description: "Internal knowledge sharing sessions every Friday",
      icon: "Presentation",
      frequency: "Weekly"
    },
    {
      title: "Open Source Contributions",
      description: "Contributing to React, Node.js, and other open source projects",
      icon: "GitBranch",
      frequency: "Monthly"
    },
    {
      title: "Conference Attendance",
      description: "Attending major tech conferences like React Conf, Node.js Interactive",
      icon: "Users",
      frequency: "Quarterly"
    },
    {
      title: "Certification Updates",
      description: "Maintaining and upgrading professional certifications",
      icon: "Award",
      frequency: "Annually"
    }
  ];

  return (
    <div className="space-y-12">
      {/* Certifications Grid */}
      <div>
        <div className="text-center mb-8">
          <h3 className="text-2xl font-bold text-foreground mb-4">Professional Certifications</h3>
          <p className="text-text-secondary max-w-2xl mx-auto">
            Our team maintains industry-leading certifications to ensure we're always working with the latest technologies and best practices.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {certifications.map((cert, index) => (
            <motion.div
              key={cert.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="card p-6 group hover:shadow-brand-lg transition-all duration-300"
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${cert.color} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                  <Icon name={cert.icon} size={24} color="white" />
                </div>
                <span className="px-2 py-1 bg-success/10 text-success text-xs font-medium rounded">
                  {cert.level}
                </span>
              </div>

              <h4 className="text-lg font-bold text-foreground mb-2 group-hover:text-primary transition-colors duration-300">
                {cert.name}
              </h4>
              <p className="text-text-secondary text-sm mb-4">{cert.provider}</p>

              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-secondary">Valid Until:</span>
                  <span className="text-foreground font-medium">
                    {new Date(cert.validUntil).toLocaleDateString()}
                  </span>
                </div>

                <div>
                  <p className="text-text-secondary text-sm mb-2">Certified Team Members:</p>
                  <div className="flex flex-wrap gap-1">
                    {cert.holders.map((holder, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 bg-primary/10 text-primary text-xs rounded"
                      >
                        {holder}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Learning Commitments */}
      <div>
        <div className="text-center mb-8">
          <h3 className="text-2xl font-bold text-foreground mb-4">Continuous Learning Commitment</h3>
          <p className="text-text-secondary max-w-2xl mx-auto">
            We believe in lifelong learning and staying ahead of the technology curve through structured learning initiatives.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {learningCommitments.map((commitment, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              viewport={{ once: true }}
              className="card p-6 group hover:shadow-brand-lg transition-all duration-300"
            >
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Icon name={commitment.icon} size={20} color="white" />
                </div>

                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-lg font-bold text-foreground group-hover:text-primary transition-colors duration-300">
                      {commitment.title}
                    </h4>
                    <span className="px-2 py-1 bg-accent/10 text-accent text-xs font-medium rounded">
                      {commitment.frequency}
                    </span>
                  </div>
                  <p className="text-text-secondary text-sm">{commitment.description}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Stats Section */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="card p-8 bg-gradient-to-r from-primary/5 to-accent/5"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <div>
            <div className="text-3xl font-bold text-primary mb-2">24+</div>
            <p className="text-text-secondary text-sm">Active Certifications</p>
          </div>
          <div>
            <div className="text-3xl font-bold text-accent mb-2">156</div>
            <p className="text-text-secondary text-sm">Learning Hours/Month</p>
          </div>
          <div>
            <div className="text-3xl font-bold text-success mb-2">12</div>
            <p className="text-text-secondary text-sm">Conferences Attended</p>
          </div>
          <div>
            <div className="text-3xl font-bold text-warning mb-2">89</div>
            <p className="text-text-secondary text-sm">Open Source Contributions</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default CertificationBadges;